import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Plus, MessageCircle, Settings } from "lucide-react";
import { api } from "@/lib/api";
import WaChat from "@/components/whatsapp/WaChat";
import WaSettings from "@/components/whatsapp/WaSettings";
import WaIcon from "@/components/whatsapp/WaIcon";
import WaQrConnect from "@/components/whatsapp/WaQrConnect";
import WaConvAvatar from "@/components/whatsapp/WaConvAvatar";
import WaCustomerList from "@/components/whatsapp/WaCustomerList";
import { QrCode } from "lucide-react";

const ago = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "");

export default function WhatsAppTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState("inbox");
    const [convs, setConvs] = useState([]);
    const [cfg, setCfg] = useState(null);
    const [q, setQ] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const [side, setSide] = useState("convs");
    const active = searchParams.get("phone") || "";

    const load = useCallback(() => api.get("/admin/whatsapp/conversations").then((r) => setConvs(r.data)), []);
    const select = (phone) => setSearchParams({ tab: "whatsapp", ...(phone ? { phone } : {}) }, { replace: true });

    const loadCfg = () => api.get("/admin/whatsapp/config").then((r) => { setCfg(r.data); return r.data; });
    useEffect(() => {
        loadCfg().then((d) => { if (!d.qr_connected && !d.api_ready && !searchParams.get("phone")) setView("connect"); });
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        const name = searchParams.get("name");
        if (active && name !== null) {
            api.post("/admin/whatsapp/conversations/start", { phone: active, name }).then((r) => { select(r.data.phone); load(); });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async (e) => {
        e.preventDefault();
        if (!newPhone.trim()) return;
        const { data } = await api.post("/admin/whatsapp/conversations/start", { phone: newPhone });
        setNewPhone("");
        await load();
        select(data.phone);
    };

    const pickCustomer = async (c) => {
        const { data } = await api.post("/admin/whatsapp/conversations/start", { phone: c.phone, name: c.name });
        await load();
        setSide("convs");
        select(data.phone);
    };

    const filtered = convs.filter((c) => `${c.name || ""} ${c.phone} ${c.last_message || ""}`.toLowerCase().includes(q.toLowerCase()));
    const conv = convs.find((c) => c.phone === active);

    return (
        <div data-testid="admin-whatsapp-tab">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2 font-display font-black uppercase text-lg"><WaIcon className="w-5 h-5 text-[#25D366]" /> WhatsApp</div>
                <div className="flex gap-1">
                    {[["inbox", "Bisedat", MessageCircle], ["connect", "Lidhja QR", QrCode], ["settings", "Cilësimet", Settings]].map(([id, l, I]) => (
                        <button key={id} onClick={() => setView(id)} className={`px-3 py-1.5 text-xs font-bold uppercase rounded-full border ${view === id ? "border-[#25D366] text-[#25D366] bg-emerald-50" : "border-slate-200 text-slate-600"}`} data-testid={`wa-view-${id}`}>
                            <I className="w-3.5 h-3.5 inline mr-1" />{l}
                        </button>
                    ))}
                </div>
            </div>
            {view === "connect" ? (
                <WaQrConnect onChange={loadCfg} />
            ) : view === "settings" ? (
                <WaSettings onSaved={setCfg} />
            ) : (
                <div className="grid md:grid-cols-[320px_1fr] border border-slate-200 rounded-xl overflow-hidden">
                    <div className={`border-r border-slate-200 ${conv ? "hidden md:block" : ""}`}>
                        <form onSubmit={start} className="flex gap-1 p-2 border-b border-slate-200">
                            <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Bisedë e re: +355…" className="input-field !py-1.5 text-xs font-mono" data-testid="wa-new-phone" />
                            <button className="btn-outline !px-2 !py-1.5" data-testid="wa-new-start"><Plus className="w-4 h-4" /></button>
                        </form>
                        <div className="relative border-b border-slate-200">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kërko…" className="w-full pl-9 pr-3 py-2 text-sm bg-transparent outline-none" data-testid="wa-search" />
                        </div>
                        <div className="grid grid-cols-2 border-b border-slate-200 text-xs font-bold uppercase">
                            {[["convs", "Bisedat"], ["customers", "Klientët"]].map(([id, l]) => (
                                <button key={id} onClick={() => setSide(id)} className={`py-2 ${side === id ? "text-[#25D366] border-b-2 border-[#25D366]" : "text-slate-500"}`} data-testid={`wa-side-${id}`}>{l}</button>
                            ))}
                        </div>
                        <div className="max-h-[62vh] overflow-y-auto">
                            {side === "customers" && <WaCustomerList q={q} onPick={pickCustomer} />}
                            {side === "convs" && filtered.length === 0 && <div className="p-6 text-center text-xs font-mono text-slate-500">Asnjë bisedë.</div>}
                            {side === "convs" && filtered.map((c) => (
                                <button key={c.phone} onClick={() => select(c.phone)} className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex gap-3 items-start hover:bg-slate-50 ${c.phone === active ? "bg-slate-100" : ""}`} data-testid={`wa-conv-${c.phone}`}>
                                    <WaConvAvatar conv={c} />
                                    <span className="flex-1 min-w-0">
                                        <span className="flex justify-between gap-2">
                                            <b className="text-sm truncate">{c.name || (c.phone.startsWith("web-") ? "Vizitor web" : `+${c.phone}`)}</b>
                                            <span className="text-[10px] font-mono text-slate-500 shrink-0">{ago(c.last_message_at)}</span>
                                        </span>
                                        <span className="flex justify-between gap-2">
                                            <span className="text-xs text-slate-500 truncate">{c.last_message || "—"}</span>
                                            {c.unread > 0 && <span className="text-[10px] font-bold bg-[#25D366] text-white rounded-full px-1.5 shrink-0" data-testid={`wa-unread-${c.phone}`}>{c.unread}</span>}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={conv ? "" : "hidden md:block"}>
                        {conv ? (
                            <>
                                <button onClick={() => select("")} className="md:hidden px-4 pt-3 text-xs font-bold uppercase text-slate-500">← Bisedat</button>
                                <WaChat key={conv.phone} conv={conv} apiReady={!!(cfg?.api_ready || cfg?.qr_connected)} quickReplies={cfg?.quick_replies || []} onQuickRepliesSaved={(q) => setCfg((c) => ({ ...c, quick_replies: q }))} onChanged={load} onDeleted={() => { select(""); load(); }} />
                            </>
                        ) : (
                            <div className="h-[70vh] grid place-items-center text-center text-sm text-slate-500 p-6">
                                <div>
                                    <WaIcon className="w-10 h-10 mx-auto mb-3 text-[#25D366]" />
                                    Zgjidhni një bisedë.
                                    {!cfg?.api_ready && !cfg?.qr_connected && <div className="text-xs mt-2 max-w-sm">WhatsApp nuk është lidhur ende: mesazhet hapen në WhatsApp-in tuaj. Lidheni me QR te "Lidhja QR".</div>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
